import { useEffect, useRef } from 'react';

import { TokenStorage } from '../utils/cookieStorage';
import { BASE_API } from '../axios';
import { convertRichPaste } from '../pages/OnlyOfficeDocuments/documentsApi';
import useOoPasteInterceptor from './useOoPasteInterceptor';

// Requests already answered by SOME mounted bridge. More than one editor
// surface can be mounted at a time (a report editor with a template modal open
// over it), and every one of them hears the plugin's postMessage — without this
// a single paste would fire two ConvertService conversions.
const handledRequests = new Set<string>();

const markHandled = (requestId: string): boolean => {
  if (handledRequests.has(requestId)) return false;
  if (handledRequests.size > 200) handledRequests.clear();
  handledRequests.add(requestId);
  return true;
};

/**
 * Host side of the Enhanced Paste OnlyOffice plugin bridge.
 *
 * The plugin (pacs-backend/public/onlyoffice-plugins/pacs-paste) runs in an
 * iframe the Document Server creates inside its own editor iframe, so it cannot
 * reach the app's auth state directly. Two things travel over postMessage:
 *
 *   1. The access token. (The message name is `pacs_dictation_token` for
 *      history's sake — it was introduced for the dictation plugin. The CSRF
 *      token and API base are additions, for the plugin's own fallback path.)
 *   2. A NARROW api proxy — one operation, no arbitrary request forwarding.
 *      Doing the HTTP call here rather than in the plugin means it goes through
 *      docsAxios, which already carries the token, the CSRF header and the
 *      right base URL. The plugin iframe's own credentials are not ours to rely
 *      on: DS sets that frame's sandbox attributes, so a same-origin cookie is
 *      not guaranteed to be sent.
 *
 * Mount this next to any DocsAPI.DocEditor instance that should offer the
 * plugin. It is a listener only — no state, no render effect.
 *
 * @param docId Optional id of the document being edited; forwarded for logging
 *              so a support call can be traced to one report.
 */
const useOoPluginBridge = (docId?: number | string | null) => {
  // The handler is registered once; the current docId is read through a ref so
  // switching documents (queue advance, surface switch) does not re-register it.
  const docIdRef = useRef(docId);
  docIdRef.current = docId;

  // One-keystroke paste (plain Ctrl+V, no modal). Mounted from here rather than
  // from each surface so that every editor that offers the Enhanced Paste plugin
  // offers the keystroke too — the two are the same feature, one with a click
  // and one without, and a surface with only one of them would be the confusing
  // case. It installs nothing unless the backend registered the inserter plugin.
  useOoPasteInterceptor(docId);

  useEffect(() => {
    const reply = (source: MessageEventSource | null, payload: Record<string, unknown>) => {
      if (!source) return;
      try {
        (source as Window).postMessage(payload, '*');
      } catch (error) {
        console.error('Failed to reply to OnlyOffice plugin:', error);
      }
    };

    const handlePluginMessage = async (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      // ── 1. Auth handshake ──
      if (data.type === 'pacs_request_dictation_token') {
        try {
          const token = TokenStorage.getAccessToken();
          if (token) {
            reply(event.source, {
              type: 'pacs_dictation_token',
              token,
              csrfToken: sessionStorage.getItem('csrf_token'),
              apiBase: BASE_API,
            });
          }
        } catch (error) {
          console.error('Failed to send dictation token:', error);
        }
        return;
      }

      // ── 2. Narrow API proxy — one operation, deliberately ──
      if (data.type === 'pacs_plugin_api' && data.requestId) {
        const { requestId, op, payload } = data;
        if (!markHandled(requestId)) return;
        const respond = (body: Record<string, unknown>) =>
          reply(event.source, { type: 'pacs_plugin_api_result', requestId, ...body });

        try {
          if (op === 'rtf-to-html') {
            const response = await convertRichPaste({
              rtf: payload?.rtf,
              html: payload?.html,
              docId: docIdRef.current ?? undefined,
            });
            respond({
              success: !!response?.data?.success,
              status: response?.status,
              data: response?.data?.data,
              message: response?.data?.message,
            });
            return;
          }

          // Anything else is not a supported operation — the plugin must not be
          // able to have arbitrary authenticated requests made on its behalf.
          respond({ success: false, message: `Unsupported plugin operation: ${op}` });
        } catch (error: any) {
          respond({
            success: false,
            status: error?.response?.status,
            message: error?.response?.data?.message || error?.message || 'Request failed',
          });
        }
      }
    };

    window.addEventListener('message', handlePluginMessage);
    return () => window.removeEventListener('message', handlePluginMessage);
  }, []);
};

export default useOoPluginBridge;
